import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router'

import FirebaseControlQuery from '@src/firebase-util/firebase-control-query'

import { TokenPayloadInterface } from '@store/actionSlices/token'
import { ProjectIdentity, RootStateTypeExtra, SessionMap } from '@store/types'

import { hasToken } from '@utilities/helper'
import useMatrixCacheCleaner from '@utilities/matrix-cache-cleaner'
import ResetUtil from '@utilities/reset-util'
import useDetectInteraction from '@utilities/useDetectInteraction'

import { ArrowPathSvg } from '@svg/react'

interface ErrorHandlerProps {
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
  token: TokenPayloadInterface
}

function ErrorHandler({ session, projectIdentity, token }: ErrorHandlerProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKeys: projectIdentity.sessionId,
  })

  const history = useHistory()
  const isConnected = !!session?.connected
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showRefreshAction, setShowRefreshAction] = useState(!isConnected)
  const { resetLocalStorage } = ResetUtil()

  const [, handleMatrixCacheCleanerTrigger, matrixCacheCleanPerformed] =
    useMatrixCacheCleaner({
      projectName: projectIdentity.projectName,
      email: token.email,
    })

  const onMatrixCacheCleanPerformed = async () => {
    if (session && isConnected) {
      await firebaseControlQuery.update({
        [`clearCacheTriggerKey`]: '',
        [`clearCacheKeyState`]: 'cleared',
      })
    }
    resetLocalStorage()
    setIsRefreshing(false)
    window.location.reload()
  }

  const handleRefreshClick = async () => {
    setIsRefreshing(true)
    if (isConnected) {
      try {
        await firebaseControlQuery.updateSessionMapOfActiveSession()
      } catch (error) {
        console.log(error)
      }
    }
    handleMatrixCacheCleanerTrigger(true)
  }

  const handleShowRefreshAction = React.useCallback(
    () => setShowRefreshAction(true),
    []
  )

  useDetectInteraction({
    disable: showRefreshAction || !isConnected,
    onInteraction: handleShowRefreshAction,
  })

  useEffect(() => {
    setShowRefreshAction(!isConnected)
  }, [isConnected])

  useEffect(() => {
    if (matrixCacheCleanPerformed) {
      history.push(`/${projectIdentity.projectName}/vision`)

      onMatrixCacheCleanPerformed().catch((err) => console.error(err))
    }
  }, [matrixCacheCleanPerformed])

  useEffect(() => {
    if (hasToken(token) || !session || !session.connected) {
      return
    }

    const { clearCacheTriggerKey: firebaseClearCacheTriggerKey } = session
    handleMatrixCacheCleanerTrigger(firebaseClearCacheTriggerKey !== '')
  }, [session?.connected, session?.clearCacheTriggerKey])

  return (
    <div className="h-screen">
      <div className="flex h-full">
        <div className="flex w-1/2 items-center justify-center bg-mainColour">
          <ArrowPathSvg className="h-32 w-32 text-white" />
        </div>
        <div className="flex w-1/2 items-center bg-white">
          <div className="mx-8">
            <h3 className="text-4xl font-bold">New updates available!</h3>
            {isConnected ? (
              <p className="mt-5 text-xl">
                {`It looks like this page hasn't loaded correctly, possibly due
                  to recent updates. From the Remote, go back to the main page or use the
                  menu to explore other sections. Alternatively, click below to
                  refresh.`}
              </p>
            ) : (
              <p className="mt-5 text-xl">
                {`It looks like this page hasn't loaded correctly, possibly due
                  to recent updates. Please go back to the main page or use the
                  menu to explore other sections. Alternatively, click below to
                  refresh.`}
              </p>
            )}
            {showRefreshAction && (
              <button
                type="button"
                className={`mt-5 rounded-md bg-[color:var(--main-colour,_#9f9fa9)] px-8 py-4 text-[18px] font-semibold leading-none text-white ${
                  isRefreshing ? 'animate-pulse' : ''
                }`}
                onClick={handleRefreshClick}
                disabled={isRefreshing}
              >
                Refresh Now
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default connect(
  ({ firestore: { session }, projectIdentity, token }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
    token,
  })
)(ErrorHandler)
